import { HOME_PROJECTS, JOURNAL_ENTRIES, projectStatus, islandLifeSummary } from './islandLife.js';
import { drawIcon } from './inventoryUi.js';
import { wrapCanvasText } from './mobileLayout.js';
import { dailyOrderStatus } from './economy.js';

const INK = '#254e46', MUTED = '#5d7662', PAPER = '#f0e6c9', LEAF = '#dde5c8';
const GOLD = '#b48a47', BORDER = '#c9ba8c';
const ICONS = { windchime: 'shells', garden: 'sweetPotato', tidepool: 'reefFish' };

export function islandLifePanelHeight(narrow, mode = 'wishes') {
  if (!narrow) return 740;
  return mode === 'journal' ? 1980 : mode === 'orders' ? 1800 : 1860;
}

function box(ctx, x, y, width, height, fill, stroke = BORDER) {
  ctx.fillStyle = fill; ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, width, height, Math.min(18, height / 2));
  else ctx.rect(x, y, width, height);
  ctx.fill();
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); }
}

function text(ctx, value, x, y, size, width, color = INK, weight = 500, align = 'start') {
  ctx.font = `${weight} ${size}px sans-serif`; ctx.fillStyle = color; ctx.textAlign = align;
  ctx.fillText(wrapCanvasText(ctx, String(value), width, 1)[0] || '', x, y);
}

function lines(ctx, value, x, y, size, width, lineHeight, maxLines = 2, color = MUTED) {
  ctx.font = `500 ${size}px sans-serif`; ctx.fillStyle = color; ctx.textAlign = 'start';
  wrapCanvasText(ctx, value, width, maxLines).forEach((line, row) => ctx.fillText(line, x, y + row * lineHeight));
}

function shortNumber(value) {
  const count = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const unit = [[1e12, '万亿'], [1e8, '亿'], [1e4, '万']].find(([amount]) => count >= amount);
  return unit ? `${Number((count / unit[0]).toFixed(1))}${unit[1]}` : String(count);
}

export function drawIslandLifePanel(ctx, state, {
  mode = 'wishes', selectedProjectId = 'windchime', narrow = false, offset = 0,
} = {}) {
  const actualMode = ['journal', 'orders'].includes(mode) ? mode : 'wishes';
  const contentHeight = islandLifePanelHeight(narrow, actualMode), buttons = [];
  const summary = islandLifeSummary(state);
  const tracked = (kind, id) => state.playerGoal?.kind === kind && state.playerGoal.id === id;
  const offers = actualMode === 'orders' && Array.isArray(state.orderBoard?.offers)
    ? state.orderBoard.offers.slice(0, 2).map(offer => dailyOrderStatus(state, offer.id)).filter(Boolean) : [];
  const button = (id, label, x, y, width, height, { active = false, disabled = false } = {}) => {
    box(ctx, x, y, width, height, disabled ? '#d6d7c3' : active ? GOLD : '#326f63', active ? '#f2d694' : '#abc4a3');
    text(ctx, label, x + width / 2, y + height / 2 + (narrow ? 14 : 9), narrow ? 40 : 24,
      width - 30, disabled ? '#637162' : '#fff6da', 700, 'center');
    buttons.push({ id, x, y: y + offset, width, height, disabled });
  };
  ctx.save(); ctx.textBaseline = 'alphabetic';
  const top = narrow ? 170 : 145, modeHeight = narrow ? 150 : 48;
  button('life-mode:wishes', '家园心愿', 30, top, 315, modeHeight, { active: actualMode === 'wishes' });
  button('life-mode:journal', '海岛手记', 355, top, 315, modeHeight, { active: actualMode === 'journal' });
  button('life-mode:orders', '海风来信', 680, top, 314, modeHeight, { active: actualMode === 'orders' });

  const compactSummary = actualMode !== 'wishes';
  const summaryY = narrow ? 336 : 207, summaryHeight = narrow ? 150 : compactSummary ? 44 : 68;
  box(ctx, 30, summaryY, 964, summaryHeight, LEAF);
  text(ctx, actualMode === 'orders' ? `海风来信 · 今天 ${offers.length} 封` : `家园 Lv.${summary.level} · ${summary.title}`,
    54, summaryY + (narrow ? 59 : compactSummary ? 18 : 29), narrow ? 43 : compactSummary ? 20 : 26, 916, INK, 700);
  text(ctx, actualMode === 'orders' ? '每天自然 06:00 更新 · 自愿交付，没有罚款'
    : actualMode === 'journal' ? `已记录 ${summary.journalCount}/${summary.journalTotal} 种 · 点选卡片追踪收集`
    : `心愿 ${summary.completed}/${summary.total} · 完成布置后，装饰永久留在家园`,
  54, summaryY + (narrow ? 117 : compactSummary ? 40 : 55), narrow ? 35 : compactSummary ? 16 : 20, 916, MUTED);

  if (actualMode === 'orders') {
    if (!offers.length) {
      const y = narrow ? 508 : 281, height = narrow ? 700 : 300;
      box(ctx, 30, y, 964, height, PAPER, GOLD);
      text(ctx, '今天暂时没有新来信', 54, y + (narrow ? 86 : 54), narrow ? 46 : 30, 916, INK, 700);
      lines(ctx, '今天也可以种植、捕鱼或布置家园。明天自然 06:00，再来读一封新信。',
      54, y + (narrow ? 190 : 111), narrow ? 40 : 24, 916, narrow ? 54 : 32, 3);
      lines(ctx, '来信可以自由决定是否完成。无需罚款，也不会扣除已有物品。',
      54, y + (narrow ? 460 : 245), narrow ? 36 : 22, 916, narrow ? 48 : 29, 2);
    }
    offers.forEach((offer, index) => {
      const y = (narrow ? 508 : 263) + index * (narrow ? 612 : 224);
      const active = tracked('order', offer.id);
      box(ctx, 30, y, 964, narrow ? 580 : 212, active ? '#f1e3b9' : PAPER, active ? GOLD : BORDER);
      text(ctx, `${offer.title}${active ? ' · 追踪中' : ''}`, 54, y + (narrow ? 61 : 30), narrow ? 46 : 25, 916, INK, 700);
      lines(ctx, offer.completed ? '谢谢岛主，货款与奖励已收下，期待下次相见。' : offer.detail,
        54, y + (narrow ? 120 : 57), narrow ? 35 : 18, 916, narrow ? 44 : 24, narrow ? 2 : 1);
      offer.requirements.slice(0, 2).forEach((requirement, row) => {
        const rowY = y + (narrow ? 195 : 73) + row * (narrow ? 90 : 34);
        const item = JOURNAL_ENTRIES.find(entry => entry.id === requirement.itemId);
        drawIcon(ctx, { icon: requirement.itemId, kind: item?.kind || 'material' }, 54, rowY, narrow ? 70 : 27);
        text(ctx, requirement.label, narrow ? 146 : 98, rowY + (narrow ? 41 : 22), narrow ? 39 : 20,
          narrow ? 285 : 370, INK, 700);
        text(ctx, `随身 ${shortNumber(requirement.held)}/${requirement.quantity} · 仓库 ${shortNumber(requirement.stored)}`,
          968, rowY + (narrow ? 41 : 22), narrow ? 35 : 18, narrow ? 515 : 500,
          requirement.held >= requirement.quantity ? '#3b7a50' : MUTED, 500, 'right');
      });
      text(ctx, `货款 ${shortNumber(offer.baseValue)} 金 + 奖励 ${shortNumber(offer.bonus)} 金 = ${shortNumber(offer.value)} 金`,
        54, y + (narrow ? 398 : 153), narrow ? 35 : 18, 916, '#7c6537');
      const buttonY = y + (narrow ? 415 : 163), buttonHeight = narrow ? 150 : 44;
      button(`life-order-track:${offer.id}`, active ? '停止追踪' : '追踪来信', 44, buttonY, 467, buttonHeight,
        { active, disabled: offer.completed });
      button(`life-order-deliver:${offer.id}`, offer.completed ? '来信已完成' : offer.available ? '前往心愿板交付' : '先备齐所需货物',
        525, buttonY, 455, buttonHeight, { active: offer.available && !offer.completed, disabled: !offer.available || offer.completed });
    });
    text(ctx, '来信自愿完成 · 收集所需货物，返航后到家园心愿板交付', 54, narrow ? 1750 : 716,
      narrow ? 28 : 16, 916, '#e8e5c7');
  } else if (actualMode === 'journal') {
    const firstY = narrow ? 508 : 263, rowHeight = narrow ? 264 : 82, rowStep = narrow ? 280 : 88;
    JOURNAL_ENTRIES.forEach((item, index) => {
      const x = 30 + index % 2 * 490, y = firstY + Math.floor(index / 2) * rowStep;
      const saved = state.islandLife?.journal?.[item.id], known = Number.isSafeInteger(saved?.count) && saved.count > 0;
      const active = tracked('journal', item.id);
      box(ctx, x, y, 474, rowHeight, active ? '#f4e1ab' : known ? PAPER : '#e0e3cf', active || known ? GOLD : BORDER);
      if (known) drawIcon(ctx, { icon: item.id, kind: item.kind }, x + 18, y + 18, narrow ? 60 : 48);
      else {
        box(ctx, x + 18, y + 18, narrow ? 60 : 48, narrow ? 60 : 48, '#849482');
        text(ctx, '?', x + (narrow ? 48 : 42), y + (narrow ? 65 : 53), narrow ? 40 : 32, 48, '#fff4d7', 700, 'center');
      }
      text(ctx, known ? item.label : item.kind === 'crop' ? '未知作物' : '未知海产', x + (narrow ? 114 : 84), y + (narrow ? 49 : 27),
        narrow ? 40 : 22, narrow ? 338 : 368, INK, 700);
      const status = known ? `累计 ${shortNumber(saved.count)} 个 · 首次第 ${shortNumber(saved.firstDay)} 天`
        : active ? '正在追踪 · 点按停止' : '尚未记录 · 点按追踪收集';
      lines(ctx, status, x + (narrow ? 22 : 84), y + (narrow ? 113 : 50), narrow ? 35 : 18, narrow ? 430 : 368,
        narrow ? 42 : 22, narrow ? 2 : 1, known ? '#7c6537' : MUTED);
      lines(ctx, item.tip, x + (narrow ? 22 : 84), y + (narrow ? 206 : 73), narrow ? 34 : 16, narrow ? 430 : 368,
        narrow ? 42 : 21, narrow ? 2 : 1);
      buttons.push({ id: `life-track:journal:${item.id}`, x, y: y + offset, width: 474, height: rowHeight, disabled: known });
    });
    text(ctx, '收获与成功捕获会记入手记 · 出售和食用后，记录仍会保留', 54, narrow ? 1940 : 716,
      narrow ? 32 : 16, 916, '#e8e5c7');
  } else {
    const selected = HOME_PROJECTS.find(item => item.id === selectedProjectId) || HOME_PROJECTS[0];
    const selectedStatus = projectStatus(state, selected.id);
    HOME_PROJECTS.forEach((definition, index) => {
      const status = projectStatus(state, definition.id), active = selected.id === definition.id;
      const x = narrow ? 30 : 30 + index * 325, y = narrow ? 508 + index * 158 : 281;
      const width = narrow ? 964 : 315, height = narrow ? 150 : 83;
      box(ctx, x, y, width, height, active ? '#efe2b6' : LEAF, active ? GOLD : BORDER);
      drawIcon(ctx, { icon: ICONS[definition.id], kind: definition.id === 'garden' ? 'crop' : definition.id === 'windchime' ? 'material' : 'seafood' },
        x + 14, y + (narrow ? 24 : 17), narrow ? 92 : 52);
      text(ctx, definition.title, x + (narrow ? 134 : 80), y + (narrow ? 59 : 36), narrow ? 44 : 25,
        width - (narrow ? 160 : 104), INK, 700);
      text(ctx, status.completed ? '已布置 · 永久保留' : tracked('wish', definition.id) ? `追踪中 · ${Math.round(status.progress * 100)}%`
        : status.locked ? '首访远岛后开启' : `已提交 ${Math.round(status.progress * 100)}%`,
        x + (narrow ? 134 : 80), y + (narrow ? 113 : 68), narrow ? 36 : 20, width - (narrow ? 160 : 104), MUTED);
      buttons.push({ id: `life-select:${definition.id}`, x, y: y + offset, width, height, disabled: false });
    });
    const detailY = narrow ? 1002 : 380, detailHeight = narrow ? 542 : 238;
    box(ctx, 30, detailY, 964, detailHeight, PAPER, GOLD);
    text(ctx, `${selected.title} · ${selectedStatus.completed ? '已完成' : selectedStatus.locked ? '待解锁' : '正在布置'}`,
      54, detailY + (narrow ? 64 : 35), narrow ? 46 : 27, 916, INK, 700);
    lines(ctx, selectedStatus.completed ? `这份心愿已完成，${selected.reward} 金奖励已领取；装饰会一直留在家园。`
      : selectedStatus.locked ? '首次登陆任意一座远岛后开启。先在海图规划航线，返航后再来布置。' : selected.detail,
    54, detailY + (narrow ? 124 : 69), narrow ? 39 : 22, 916, narrow ? 49 : 29, narrow ? 2 : 1);
    text(ctx, '材料来自随身背包与建材 · 仓库物品先取回 · 可分次提交', 54, narrow ? 1221 : 473,
      narrow ? 33 : 19, 916, MUTED);
    selectedStatus.requirements.forEach((requirement, index) => {
      const y = (narrow ? 1275 : 500) + index * (narrow ? 62 : 30);
      text(ctx, requirement.label, 56, y + (narrow ? 30 : 21), narrow ? 39 : 22, narrow ? 395 : 400, INK, 700);
      text(ctx, `已交 ${requirement.contributed}/${requirement.required} · 随身 ${shortNumber(requirement.held)}`,
        968, y + (narrow ? 30 : 21), narrow ? 35 : 22, narrow ? 505 : 490,
        requirement.contributed >= requirement.required ? '#3b7a50' : MUTED, 500, 'right');
    });
    const progressY = narrow ? 1570 : 633;
    box(ctx, 30, progressY, 964, narrow ? 18 : 12, '#566b50', null);
    if (selectedStatus.progress > 0) box(ctx, 30, progressY, 964 * selectedStatus.progress, narrow ? 18 : 12, '#dfc47b', null);
    const disabled = !selectedStatus.available || selectedStatus.completed || selectedStatus.locked;
    const actionLabel = selectedStatus.completed ? '已完成 · 装饰永久保留' : selectedStatus.locked ? '先首次登陆一座远岛'
      : selectedStatus.available ? '前往心愿板提交' : '先收集缺少的材料';
    button(`life-track:wish:${selected.id}`, tracked('wish', selected.id) ? '停止追踪' : '追踪心愿', 30, narrow ? 1640 : 655,
      474, narrow ? 150 : 46, { active: tracked('wish', selected.id), disabled: selectedStatus.completed });
    button(`life-contribute:${selected.id}`, actionLabel, 520, narrow ? 1640 : 655, 474, narrow ? 150 : 46, { active: !disabled, disabled });
    text(ctx, selectedStatus.completed ? `家园 Lv.${summary.level} · ${summary.title} · 可继续实现其他心愿`
      : `完成奖励 ${selected.reward} 金 · 返航后，在小屋外的心愿板交付材料`,
    54, narrow ? 1828 : 720, narrow ? 28 : 16, 916, '#e8e5c7');
  }
  ctx.restore();
  return { buttons, contentHeight };
}
